import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  saveSkuCommands,
  saveSkuTicketSettings,
  saveSkuWorkflows,
  skuCommandsTabQuery,
  skuOperations,
  skuSubcategories,
  skuTicketSettingsQuery,
  skuWorkflowsQuery,
  type TicketSettingsForm,
  type TicketSettingsPanel,
  type WorkflowsPanel,
} from '../../api/settingsEaseedeskSkus'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Cele trei file din fisa unui SKU: Commands, Ticket settings, Workflows.
 *
 * Element cu element fata de `version-3/easeedesk_settings/skus/
 * commands_skus.php`, `tickets_settings_skus.php` si `workflows_sku.php` -
 * trei machete de peste o mie de randuri, mutate impreuna fiindca se citesc
 * una pe alta: fila Workflows nu se poate desena pana cand Ticket settings
 * n-are un tip ales, si niciuna nu stie de panoul de operator pana nu se uita
 * la comenzile SKU-ului.
 *
 * Toate propozitiile si titlurile sunt cele din machetele vechi, cuvant cu
 * cuvant, inclusiv "Please make sure you have set a operator ticket first."
 * - scris asa acolo.
 */

function errorMessage(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/* =============================================================================
 *  Commands
 * ========================================================================== */

interface Pick {
  id: number
  title: string
}

/**
 * O pereche de liste, ca in macheta: in stanga ce se poate adauga, in dreapta
 * ce e pus, cu sagetile de mutat pe rand.
 *
 * Selectia se face din clic pe rand, fara casute - la fel ca in ecranul vechi,
 * unde `li.active` tinea alegerea. Sagetile de ordonare se vad numai pe randul
 * ales, tot ca acolo.
 */
function DualList({
  title,
  badge,
  available,
  active,
  onChange,
}: {
  title: string
  badge: string
  available: Pick[]
  active: Pick[]
  onChange(available: Pick[], active: Pick[]): void
}) {
  const [leftPicked, setLeftPicked] = useState<number[]>([])
  const [rightPicked, setRightPicked] = useState<number[]>([])

  const toggle = (list: number[], id: number) =>
    list.includes(id) ? list.filter((one) => one !== id) : [...list, id]

  const add = () => {
    const moved = available.filter((one) => leftPicked.includes(one.id))
    onChange(
      available.filter((one) => !leftPicked.includes(one.id)),
      [...active, ...moved],
    )
    setLeftPicked([])
  }

  const remove = () => {
    const moved = active.filter((one) => rightPicked.includes(one.id))
    onChange(
      [...available, ...moved],
      active.filter((one) => !rightPicked.includes(one.id)),
    )
    setRightPicked([])
  }

  const move = (id: number, by: number) => {
    const at = active.findIndex((one) => one.id === id)
    const to = at + by
    if (at < 0 || to < 0 || to >= active.length) return
    const next = [...active]
    const [taken] = next.splice(at, 1)
    next.splice(to, 0, taken)
    onChange(available, next)
  }

  const box = { height: 200, overflowY: 'scroll' as const, paddingLeft: 0, listStyle: 'none', margin: 0 }
  const row = (picked: boolean) => ({
    padding: '4px 8px',
    cursor: 'pointer',
    background: picked ? '#eef3f8' : undefined,
  })

  return (
    <div className="row" style={{ marginBottom: 20 }}>
      <div className="col-md-6 col_option">
        <h5>
          <span className={`label ${badge}`}>
            {title} ({available.length} available)
          </span>
        </h5>
        <button type="button" className="btn btn-xs btn-success" disabled={leftPicked.length === 0} onClick={add}>
          Add the selected commands <span className="badge count_selcomm">{leftPicked.length}</span>{' '}
          <i className="fas fa-angle-right" aria-hidden="true"></i>
        </button>
        <ul className="commands_sku" style={box}>
          {available.map((one) => (
            <li
              key={one.id}
              data-value={one.id}
              style={row(leftPicked.includes(one.id))}
              onClick={() => setLeftPicked(toggle(leftPicked, one.id))}
            >
              <a>{one.title}</a>
            </li>
          ))}
        </ul>
      </div>

      <div className="col-md-6 col_select">
        <h5 className="pull-right">
          <span className={`label ${badge}`}>{title} (Active)</span>
        </h5>
        <div style={{ clear: 'both' }}></div>
        <button type="button" className="btn btn-xs btn-danger" disabled={rightPicked.length === 0} onClick={remove}>
          <i className="fas fa-angle-left" aria-hidden="true"></i> Remove the selected commands{' '}
          <span className="badge count_selcomm">{rightPicked.length}</span>
        </button>
        <ul className="commands_sku res_commands_sku" style={box}>
          {active.map((one, at) => (
            <li
              key={one.id}
              data-value={one.id}
              style={{ ...row(rightPicked.includes(one.id)), position: 'relative' }}
              onClick={() => setRightPicked(toggle(rightPicked, one.id))}
            >
              <a>{one.title}</a>
              {rightPicked.includes(one.id) && (
                <span className="move" style={{ position: 'absolute', right: 8, top: 4 }}>
                  <i
                    className="fas fa-chevron-up"
                    style={{ cursor: 'pointer', opacity: at === 0 ? 0.3 : 1 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      move(one.id, -1)
                    }}
                  ></i>{' '}
                  <i
                    className="fas fa-chevron-down"
                    style={{ cursor: 'pointer', opacity: at === active.length - 1 ? 0.3 : 1 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      move(one.id, 1)
                    }}
                  ></i>
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function SkuCommandsTab({ sku, onSaved }: { sku: number; onSaved(): void }) {
  const tab = useQuery(skuCommandsTabQuery(sku))

  const [standard, setStandard] = useState<{ available: Pick[]; active: Pick[] }>({ available: [], active: [] })
  const [operator, setOperator] = useState<{ available: Pick[]; active: Pick[] }>({ available: [], active: [] })

  useEffect(() => {
    if (!tab.data) return
    const pick = (rows: { ID?: number; command_id?: number; command_title: string }[]): Pick[] =>
      rows.map((one) => ({ id: one.command_id ?? one.ID ?? 0, title: one.command_title }))
    setStandard({ available: pick(tab.data.standard.available), active: pick(tab.data.standard.active) })
    setOperator({ available: pick(tab.data.operator.available), active: pick(tab.data.operator.active) })
  }, [tab.data])

  const save = useMutation({
    /* Standard intai, apoi operator: ordinea din lista E ordinea de executie,
       si pe ea se sprijina propozitia de sus a filei. */
    mutationFn: () =>
      saveSkuCommands(sku, [...standard.active.map((one) => one.id), ...operator.active.map((one) => one.id)]),
    onSuccess: () => {
      toast('Commands successfully saved for this SKU.', 'Success')
      onSaved()
    },
    onError: (error) => toast(errorMessage(error), 'Error', 'error'),
  })

  if (tab.error) return <div className="alert alert-danger">{errorMessage(tab.error)}</div>
  if (!tab.data) return <Spinner />

  return (
    <div>
      <div className="alert alert-warning">
        <i className="fas fa-info-circle" aria-hidden="true"></i> {tab.data.note}
      </div>

      <DualList
        title="Standard Commands"
        badge="label-warning"
        available={standard.available}
        active={standard.active}
        onChange={(available, active) => setStandard({ available, active })}
      />
      <DualList
        title="Operator Commands"
        badge="label-info"
        available={operator.available}
        active={operator.active}
        onChange={(available, active) => setOperator({ available, active })}
      />

      <hr className="dotted" />
      <div style={{ textAlign: 'center' }}>
        <button type="button" className="btn btn-success" disabled={save.isPending} onClick={() => save.mutate()}>
          Save
        </button>
      </div>
    </div>
  )
}

/* =============================================================================
 *  Ticket settings
 * ========================================================================== */

const EMPTY_TICKET: TicketSettingsForm = {
  ticket_type: 0,
  category: 0,
  subcategory: 0,
  operation: 0,
  priority: 0,
  group: 0,
}

function TicketSettingsPanelForm({
  sku,
  panel,
  columns,
  options,
  onSaved,
}: {
  sku: number
  panel: TicketSettingsPanel
  /** Latimea panoului, ca `$col_tickets_type` din macheta: 6 singur, 4 cand e si operatorul. */
  columns: number
  options: {
    ticket_types: { ID: number; type_name: string }[]
    categories: { ID: number; category_name: string }[]
    priorities: { ID: number; priority_name: string }[]
    groups: { ID: number; name: string }[]
  }
  onSaved(): void
}) {
  const [form, setForm] = useState<TicketSettingsForm>(EMPTY_TICKET)
  const [subcategories, setSubcategories] = useState(panel.subcategories)
  const [operations, setOperations] = useState(panel.operations)
  const [loading, setLoading] = useState<'' | 'subcategory' | 'operation'>('')

  useEffect(() => {
    setForm(panel.saved)
    setSubcategories(panel.subcategories)
    setOperations(panel.operations)
  }, [panel])

  const chooseCategory = async (category: number) => {
    setForm((was) => ({ ...was, category, subcategory: 0, operation: 0 }))
    setOperations([])
    if (category < 1) {
      setSubcategories([])
      return
    }
    setLoading('subcategory')
    try {
      setSubcategories((await skuSubcategories(category)).subcategories)
    } catch (error) {
      toast(errorMessage(error), 'Error', 'error')
    } finally {
      setLoading('')
    }
  }

  const chooseSubcategory = async (subcategory: number) => {
    setForm((was) => ({ ...was, subcategory, operation: 0 }))
    if (subcategory < 1) {
      setOperations([])
      return
    }
    setLoading('operation')
    try {
      setOperations((await skuOperations(subcategory)).operations)
    } catch (error) {
      toast(errorMessage(error), 'Error', 'error')
    } finally {
      setLoading('')
    }
  }

  const save = useMutation({
    mutationFn: () => saveSkuTicketSettings(sku, panel.kind, form),
    onSuccess: () => {
      toast('Ticket saved successfully.', 'Success')
      onSaved()
    },
    onError: (error) => toast(errorMessage(error), 'Error', 'error'),
  })

  /* "loading..." / "- no results available -" sunt textele machetei vechi:
     un selector gol fara ele arata la fel cu unul care inca se incarca. */
  const placeholder = (busy: boolean, rows: unknown[]) =>
    busy ? 'loading...' : rows.length === 0 ? '- no results available -' : '- select -'

  return (
    <div className={`col-md-${columns}`}>
      <div className="panel panel-default">
        <div className="panel-heading">
          <span className={`label ${panel.label}`}>{panel.title}</span>
        </div>
        <div className="panel-body">
          <form
            className="form-ticket-settings-edit"
            method="post"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            <div className="row">
              <div className="col-md-12">
                <div className="form-group">
                  <label className="control-label">Ticket Type</label>
                  <select
                    className="form-control input-sm"
                    name="ticket_type"
                    value={form.ticket_type || ''}
                    onChange={(e) => setForm({ ...form, ticket_type: Number(e.target.value) })}
                  >
                    <option value="">- select -</option>
                    {options.ticket_types.map((one) => (
                      <option key={one.ID} value={one.ID}>
                        {one.type_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Category</label>
                  <select
                    className="form-control input-sm category_select_ss"
                    name="category"
                    value={form.category || ''}
                    onChange={(e) => void chooseCategory(Number(e.target.value))}
                  >
                    <option value="">- select -</option>
                    {options.categories.map((one) => (
                      <option key={one.ID} value={one.ID}>
                        {one.category_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Subcategory</label>
                  <select
                    className="form-control input-sm subcategory_select_ss"
                    name="subcategory"
                    value={form.subcategory || ''}
                    onChange={(e) => void chooseSubcategory(Number(e.target.value))}
                  >
                    <option value="">{placeholder(loading === 'subcategory', subcategories)}</option>
                    {subcategories.map((one) => (
                      <option key={one.ID} value={one.ID}>
                        {one.subcategory_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="form-group">
                  <label className="control-label">Operation</label>
                  <select
                    className="form-control input-sm subcat_operation_select_ss"
                    name="operation"
                    value={form.operation || ''}
                    onChange={(e) => setForm({ ...form, operation: Number(e.target.value) })}
                  >
                    <option value="">{placeholder(loading === 'operation', operations)}</option>
                    {operations.map((one) => (
                      <option key={one.ID} value={one.ID}>
                        {one.operation_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Priority</label>
                  <select
                    className="form-control input-sm"
                    name="priority"
                    value={form.priority || ''}
                    onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                  >
                    <option value="">- select -</option>
                    {options.priorities.map((one) => (
                      <option key={one.ID} value={one.ID}>
                        {one.priority_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Group</label>
                  <select
                    className="form-control input-sm"
                    name="group"
                    value={form.group || ''}
                    onChange={(e) => setForm({ ...form, group: Number(e.target.value) })}
                  >
                    <option value="">- select -</option>
                    {options.groups.map((one) => (
                      <option key={one.ID} value={one.ID}>
                        {one.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <hr className="dotted" />
            <div style={{ textAlign: 'center' }}>
              <button type="submit" className="btn btn-success save-ticket-sku" disabled={save.isPending}>
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export function SkuTicketSettingsTab({ sku, onSaved }: { sku: number; onSaved(): void }) {
  const tab = useQuery(skuTicketSettingsQuery(sku))

  if (tab.error) return <div className="alert alert-danger">{errorMessage(tab.error)}</div>
  if (!tab.data) return <Spinner />

  return (
    <div className="row">
      {tab.data.panels.map((panel) => (
        <TicketSettingsPanelForm
          key={panel.kind}
          sku={sku}
          panel={panel}
          columns={tab.data.panels.length > 1 ? 4 : 6}
          options={tab.data}
          onSaved={onSaved}
        />
      ))}
    </div>
  )
}

/* =============================================================================
 *  Workflows
 * ========================================================================== */

function WorkflowsPanelForm({
  sku,
  panel,
  skuStatuses,
  onSaved,
}: {
  sku: number
  panel: WorkflowsPanel
  skuStatuses: { id: number; name: string; class: string }[]
  onSaved(): void
}) {
  const [chosen, setChosen] = useState<Record<number, number>>({})

  useEffect(() => {
    const next: Record<number, number> = {}
    for (const [skuStatus, ticketStatus] of Object.entries(panel.chosen)) next[Number(skuStatus)] = ticketStatus
    setChosen(next)
  }, [panel])

  const save = useMutation({
    mutationFn: () => saveSkuWorkflows(sku, panel.kind, chosen),
    onSuccess: () => {
      toast('Workflow saved successfully.', 'Success')
      onSaved()
    },
    onError: (error) => toast(errorMessage(error), 'Error', 'error'),
  })

  return (
    <div className="col-md-4">
      <div className="panel panel-default">
        <div className="panel-heading">
          <span className={`label ${panel.label}`}>{panel.title}</span>
        </div>
        <div className="panel-body">
          <form
            method="post"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            {skuStatuses.map((status) => (
              <div className="input-group" key={status.id} style={{ marginBottom: 6 }}>
                <span className="input-group-addon input-sm btn btn-default">
                  <span className={`label label-${status.class}`}>{status.name}</span>
                </span>
                <select
                  className="form-control input-sm"
                  name={String(status.id)}
                  value={chosen[status.id] || ''}
                  onChange={(e) => {
                    const value = Number(e.target.value)
                    setChosen((was) => {
                      const next = { ...was }
                      /* Neales inseamna nelegat: cheia dispare, si salvarea
                         sterge randul - "nimic ales" e un raspuns bun. */
                      if (value > 0) next[status.id] = value
                      else delete next[status.id]
                      return next
                    })
                  }}
                >
                  <option value="">- select -</option>
                  {panel.statuses.map((one) => (
                    <option key={one.ID} value={one.ID}>
                      {one.status_name}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            <hr className="dotted" />
            <div style={{ textAlign: 'center' }}>
              <button type="submit" className="btn btn-success save-ticket-sku" disabled={save.isPending}>
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export function SkuWorkflowsTab({ sku, onSaved }: { sku: number; onSaved(): void }) {
  const tab = useQuery(skuWorkflowsQuery(sku))

  if (tab.error) return <div className="alert alert-danger">{errorMessage(tab.error)}</div>
  if (!tab.data) return <Spinner />

  return (
    <div id="wrap_workflows_tab_skus">
      {tab.data.warnings.map((warning) => (
        <div className="alert alert-warning" key={warning}>
          <i className="fas fa-info-circle" aria-hidden="true"></i> {warning}
        </div>
      ))}

      <div className="row">
        {tab.data.panels.map((panel) => (
          <WorkflowsPanelForm
            key={panel.kind}
            sku={sku}
            panel={panel}
            skuStatuses={tab.data.sku_statuses}
            onSaved={onSaved}
          />
        ))}
      </div>
    </div>
  )
}
